import React, { useContext, useMemo, useRef } from 'react';
import { Control } from '../../../../';
import { IFormContext } from '../../interfaces/IFormContext';
import { FormContext } from '../../Form';
import { Form } from "../../interfaces/form";
import { Icon, Label, mergeStyles, PrimaryButton, useTheme } from '@fluentui/react';
import { getRequiredLevel } from '@ComponentFramework/PropertyClasses/Context';
import { FormControlType } from '../../interfaces/enums';
import { getCellStyles } from './styles';
import { Text } from '@fluentui/react/lib/Text';

export interface ICellProps {
    cell: Form.Cell;
    index?: number;
    tabContentType?: 'cardSections' | 'singleComponent',
    formContext?: IFormContext;
    theme?: ComponentFramework.FluentDesignState;
    cellLabelPosition?: 'Top' | 'Left'
    onFormUpdated: (arg: { formContext: Xrm.FormContext }) => void;
};

export const Cell: React.FC<ICellProps> = (props) => {
    const formContext: IFormContext = props.formContext ?? useContext(FormContext);
    const theme = useTheme();
    const cell = props.cell;
    const control = props.cell.control;
    const ref = useRef<HTMLDivElement>(null);
    const [wrapped, setWrapped] = React.useState(false);
    const [notificationVisible, setNotificationVisible] = React.useState(false);
    let dataIdClass = "fieldControl_container";
    const lockIconClassName = 'TALXIS__form__cell__lockIcon';
    const requiredLevel = getRequiredLevel(formContext.attributeConfiguration[control?.datafieldname], control?.isRequired);
    const styles = useMemo(() => getCellStyles(theme), [theme]);
    if (cell?.control?.classId.toLowerCase() === "{4273edbd-ac1d-40d3-9fb2-095c621b552d}") {
        dataIdClass = "fieldControl-text-box-container";
    }

    const handleToggleWrap = () => {
        setWrapped(prev => !prev);
    };

    const labelVisible = () => {
        if (props.tabContentType === 'singleComponent' && props.index === 0) {
            return false;
        }
        if (cell?.label && cell?.showLabel && cell?.control && cell?.control?.bindings?.EnableViewPicker?.value !== 'true' && cell?.control?.name !== 'TALXIS.PCF.Portal.Label') {
            return true;
        }
        return false;
    };
    const shouldDisplay = () => {
        if (((control && (control.visible ?? true)) || (!cell.control && (cell?.visible ?? true))) && (!formContext.isMobile || (cell?.availableForPhone ?? true))) {
            return true;
        }
        return false;
    };
    const isMultipleOrSubgrid = () => {
        const bindingTypeGroup = cell?.control?.definition?.manifest.properties[0]?.ofTypeGroup;
        if (cell?.control?.definition?.manifest.properties[0]?.ofType === 'Multiple') {
            return true;
        }
        if (bindingTypeGroup && cell?.control?.definition?.manifest.typeGroups.find(typeGroup => typeGroup.name === bindingTypeGroup && typeGroup.types.includes('Multiple')))
            if (cell?.control?.definition?.manifest.typeGroups.find(typeGroup => typeGroup.types.includes('Multiple'))) {
                return true;
            }
        if (cell.control?.name === 'TALXIS.PCF.Portal.View') {
            return true;
        }
        return false;
    };
    if (props.tabContentType === 'singleComponent' && props.index !== 0) {
        return <></>;
    }
    const getRowEnd = (): string | undefined => {
        if (!cell.rowspan || cell.rowspan === '1') {
            return undefined;
        }
        return `span ${parseInt(cell.rowspan) * 3}`;
    };

    const isLabelSuffixVisible = () => {
        switch (true) {
            case requiredLevel === 1:
            case requiredLevel === 2:
            case isNotificationIconVisible(): {
                return true;
            }
            default: {
                return false;
            }
        }
    };

    const isNotificationIconVisible = () => {
        return props.cell.control?.notifications && props.cell.control?.notifications?.length > 0;
    };

    return (
        //In dialogs controls may not be defined for label placeholders and in that case labels should be displayed
        <>
            <div ref={ref} style={cell.control?.type === FormControlType.DataSet ? {
                '--cell-display-mode': 'block',
                '--cell-label-width': 'initial'
            } as React.CSSProperties : undefined} data-is-multiple-line={isMultipleOrSubgrid()} data-use-available-vertical-space={cell.auto} className={`TALXIS__form__cell ${mergeStyles({
                gridColumnEnd: cell.colspan && `span ${cell.colspan}`,
                gridRowEnd: getRowEnd(),
                '.TALXIS__form__cell__required-indicator': {
                    color: theme.semanticColors.errorIcon,
                    marginLeft: 3
                },
                '>div>label>[data-icon-name^="Lock"]': {
                    visibility: control?.disabled ? 'visible' : 'hidden'
                }

            })}`}>
                {/* //we always need to render the top level cell element even if it's not visible
                //if we do not, the layout with spacers might get broken as we do not seperate between rows
                //in HTML (to allow rowspan for elements), if the cells disappears the spacers might appear in different row which break the layout */}
                {shouldDisplay() &&
                    <div className={mergeStyles({ height: notificationVisible ? 'initial !important' : undefined })}>
                        {(labelVisible() || isNotificationIconVisible()) &&
                            <>
                                <Label title={cell.label}
                                    className={`TALXIS__form__cell__label ${wrapped && props.cellLabelPosition === 'Left' ? 'TALXIS__form__cell__label--wrap' : 'TALXIS__form__cell__label--nowrap'}`}>
                                    {labelVisible() &&
                                        <>
                                            <Icon className={`${lockIconClassName}${!control.disabled ? ` ${lockIconClassName}--hidden` : ''}`} iconName='Lock12' />
                                            <span className={mergeStyles({ cursor: 'pointer' })} onClick={handleToggleWrap}>{cell.label}</span>
                                        </>
                                    }
                                    {isLabelSuffixVisible() &&
                                        <div className={styles.labelSuffixWrapper}>
                                            {(requiredLevel === 1 || requiredLevel === 2) &&
                                                <span className='TALXIS__form__cell__required-indicator'>*</span>
                                            }
                                            {isNotificationIconVisible() &&
                                                <Icon
                                                    iconName='Lightbulb'
                                                    onClick={() => setNotificationVisible(!notificationVisible)}
                                                    styles={{
                                                        root: styles.notificationIconRoot
                                                    }} />
                                            }
                                        </div>
                                    }
                                </Label>
                            </>
                        }
                        {cell?.control &&
                            <div data-id={`${cell.control?.id}.${dataIdClass}`}>
                                <Control cellId={cell.id} formContext={formContext}
                                    {...cell.control}
                                    theme={props.theme}
                                    bindings={{
                                        ...cell.control.bindings,
                                        ...(props.tabContentType === 'singleComponent' ? {
                                            Height: {
                                                isStatic: true,
                                                value: '100%'
                                            }
                                        } : undefined)
                                    }}
                                    label={cell.label}
                                    childeventlisteners={cell.control.name?.includes('PCF.Portal.Form') ? [
                                        {
                                            eventname: "__onFormUpdated",
                                            eventhandler: props.onFormUpdated
                                        }
                                    ] : undefined} />
                            </div>
                        }
                    </div>
                }
                {notificationVisible &&
                    <div className={styles.notificationWrapper}>
                        <Label>
                            <Icon className={`${lockIconClassName} ${lockIconClassName}--hidden ${mergeStyles({ visibility: 'hidden !important' })}`} iconName='Lock12' />
                            {window.TALXIS.Portal.Translations.getLocalizedString('components/forms/Recommendation')}
                        </Label>
                        {(() => {
                            const notification = props.cell.control?.notifications?.[0];
                            const title = notification.actions[0]?.message;
                            const message = notification.messages?.[0];
                            const executableAction = notification.actions?.[0]?.actions?.[0];

                            return <div className={styles.notificationContent}>
                                {title &&
                                    <Label className={styles.notificationMessageTitle}>{title}</Label>
                                }
                                {message &&
                                    <Text className={!title ? styles.notificationMessageTitle : undefined}>{message}</Text>
                                }
                                {executableAction &&
                                    <PrimaryButton
                                        text={window.TALXIS.Portal.Translations.getLocalizedString('components/forms/Apply')}
                                        styles={{
                                            root: styles.notificationApplyButtonRoot,
                                        }}
                                        onClick={() => {
                                            setNotificationVisible(false);
                                            try {
                                                executableAction?.();
                                            }
                                            catch (e) {
                                                console.error('Error executing action:', e);
                                            }
                                        }} />
                                }
                            </div>;

                        })()}
                    </div>

                }
            </div>
        </>
    );
};
export default Cell;